(() => {
  if (customElements.get('wohnjoy-mobile-menu')) return;
  class WohnjoyMobileMenu extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.panel = this.querySelector('.wj-mobile-panel');
      this.header = this.closest('header') || document.querySelector('header');
      if (!this.panel || !this.header) return;
      this.headerSection = this.header.closest('.section-header') || this.header;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.mobile = window.matchMedia('(max-width: 1024.98px)');
      this.triggers = Array.from(document.querySelectorAll('.nav-toggle, [data-action="toggle-nav"]'));
      this.originalAttributes = new Map(this.triggers.map(trigger => [trigger,
        Object.fromEntries(['role', 'tabindex', 'aria-label', 'aria-expanded', 'aria-controls', 'aria-haspopup'].map(name => [name, trigger.getAttribute(name)]))
      ]));
      this.originalIcons = new Map();
      this.updateTriggers();
      this.scheduleImages();
      this.triggers.forEach(trigger => {
        trigger.addEventListener('pointerdown', () => this.prepareImages(), {signal, passive: true});
        trigger.addEventListener('focusin', () => this.prepareImages(6), {signal});
      });
      document.addEventListener('click', event => {
        if (!this.mobile.matches) return;
        const trigger = event.target.closest?.('.nav-toggle, [data-action="toggle-nav"]');
        if (trigger) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.isOpen ? this.close() : this.open(trigger);
        } else if (this.isOpen && this.header.contains(event.target) && event.target.closest('a, button, [role="button"], .action')) {
          this.close(false);
        }
      }, {capture: true, signal});
      document.addEventListener('keydown', event => {
        if (!this.mobile.matches) return;
        const trigger = event.target.closest?.('.nav-toggle, [data-action="toggle-nav"]');
        if (trigger && ['Enter', ' '].includes(event.key)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.isOpen ? this.close() : this.open(trigger);
        } else if (this.isOpen && event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.close();
        } else if (this.isOpen && event.key === 'Tab') {
          const selector = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
          const focusable = [...this.header.querySelectorAll(selector), ...this.panel.querySelectorAll(selector)]
            .filter(el => el.tabIndex >= 0 && el.getClientRects().length && !el.closest('[inert]'));
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (!first) return;
          if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
            event.preventDefault(); first.focus();
          }
        }
      }, {capture: true, signal});
      this.panel.addEventListener('click', event => {
        if (event.target.closest('a[href]')) this.close(false);
      }, {signal});
      this.panel.querySelectorAll('.wj-mobile-menu__room').forEach(room => {
        room.addEventListener('toggle', () => {
          if (!room.open) return;
          this.panel.querySelectorAll('.wj-mobile-menu__room').forEach(other => {
            if (other !== room) other.open = false;
          });
          requestAnimationFrame(() => {
            if (!this.isOpen || !room.open) return;
            const scroll = this.panel.querySelector('.wj-mobile-menu__scroll');
            scroll.scrollTop = Math.max(0, scroll.scrollTop + room.getBoundingClientRect().top - scroll.getBoundingClientRect().top - 10);
          });
        }, {signal});
      });
      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches) this.close(false);
        this.updateTriggers();
        this.scheduleImages();
      }, {signal});
      window.addEventListener('resize', () => this.positionPanel(), {signal});
      window.visualViewport?.addEventListener('resize', () => this.positionPanel(), {signal});
      this.resizeObserver = new ResizeObserver(() => this.positionPanel());
      this.resizeObserver.observe(this.header);
    }
    get isOpen() { return this.panel && !this.panel.hidden; }
    updateTriggers() {
      this.triggers.forEach(trigger => {
        if (!this.mobile.matches) return this.restoreTrigger(trigger);
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('tabindex', '0');
        trigger.setAttribute('aria-label', this.isOpen ? this.dataset.closeLabel : this.panel.getAttribute('aria-label'));
        trigger.setAttribute('aria-controls', this.panel.id);
        trigger.setAttribute('aria-expanded', String(this.isOpen));
        this.updateIcon(trigger, Boolean(this.isOpen));
      });
    }
    updateIcon(trigger, open) {
      const icon = trigger.querySelector('svg');
      if (!icon) return;
      if (open && !this.originalIcons.has(icon)) {
        this.originalIcons.set(icon, {html: icon.innerHTML, viewBox: icon.getAttribute('viewBox'), height: icon.getAttribute('height')});
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('height', '22');
        icon.innerHTML = '<path d="m5 5 14 14M19 5 5 19" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>';
      } else if (!open && this.originalIcons.has(icon)) {
        const original = this.originalIcons.get(icon);
        icon.innerHTML = original.html;
        for (const name of ['viewBox', 'height']) {
          original[name] === null ? icon.removeAttribute(name) : icon.setAttribute(name, original[name]);
        }
        this.originalIcons.delete(icon);
      }
    }
    restoreTrigger(trigger) {
      this.updateIcon(trigger, false);
      Object.entries(this.originalAttributes.get(trigger)).forEach(([name, value]) => {
        value === null ? trigger.removeAttribute(name) : trigger.setAttribute(name, value);
      });
    }
    positionPanel() {
      if (!this.isOpen) return;
      if (this.lockedLayout) {
        const width = window.innerWidth - this.lockedLayout.scrollbar;
        const scale = width / this.lockedLayout.width;
        document.body.style.setProperty('--wj-page-width', `${width}px`);
        this.headerSection.style.setProperty('--wj-header-left', `${this.lockedLayout.left * scale}px`);
        this.headerSection.style.setProperty('--wj-header-width', `${this.lockedLayout.headerWidth * scale}px`);
      }
      const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
      this.panel.style.setProperty('--wj-menu-top', `${bottom}px`);
    }
    scheduleImages() {
      if (!this.mobile.matches || !this.panel.querySelector('template[data-wj-image]') || this.imagesScheduled) return;
      this.imagesScheduled = true;
      const signal = this.controller.signal;
      const warm = () => {
        this.imagesScheduled = false;
        if (signal.aborted || !this.mobile.matches) return;
        this.prepareImages(6);
        this.scheduleImages();
      };
      const schedule = () => {
        if (signal.aborted) return;
        if (window.requestIdleCallback) this.imagesIdle = window.requestIdleCallback(warm, { timeout: 2000 });
        else this.imagesTimer = window.setTimeout(warm, 100);
      };
      if (document.readyState === 'complete') schedule();
      else window.addEventListener('load', schedule, { once: true, signal });
    }
    prepareImages(limit = Infinity) {
      if (!this.mobile.matches) return;
      [...this.panel.querySelectorAll('template[data-wj-image]')].slice(0, limit).forEach(template => {
        template.replaceWith(template.content.cloneNode(true));
      });
    }
    lockLayout() {
      const rect = this.headerSection.getBoundingClientRect();
      const width = document.documentElement.clientWidth || window.innerWidth;
      this.lockedLayout = {width, scrollbar: window.innerWidth - width, left: rect.left, headerWidth: rect.width};
      this.styleSnapshots = [
        [document.body, ['--wj-page-top', '--wj-page-width']],
        [this.headerSection, ['--wj-header-top', '--wj-header-left', '--wj-header-width']]
      ].map(([element, names]) => [element, names.map(name => [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)])]);
      const position = getComputedStyle(this.headerSection).position;
      if (!['fixed', 'absolute'].includes(position)) {
        this.spacer = document.createElement('div');
        this.spacer.setAttribute('aria-hidden', 'true');
        this.spacer.style.height = `${rect.height}px`;
        this.headerSection.before(this.spacer);
      }
      document.body.style.setProperty('--wj-page-top', `${-this.scrollPosition}px`);
      document.body.style.setProperty('--wj-page-width', `${width}px`);
      this.headerSection.style.setProperty('--wj-header-top', `${Math.max(0, rect.top)}px`);
      this.headerSection.style.setProperty('--wj-header-left', `${rect.left}px`);
      this.headerSection.style.setProperty('--wj-header-width', `${rect.width}px`);
      this.headerSection.classList.add('wj-menu-header-active');
      document.documentElement.classList.add('wohnjoy-mobile-menu-open');
    }
    unlockLayout() {
      this.headerSection.classList.remove('wj-menu-header-active');
      document.documentElement.classList.remove('wohnjoy-mobile-menu-open');
      this.spacer?.remove();
      this.spacer = null;
      this.styleSnapshots?.forEach(([element, properties]) => properties.forEach(([name, value, priority]) => {
        value ? element.style.setProperty(name, value, priority) : element.style.removeProperty(name);
      }));
      this.lockedLayout = null;
    }
    open(trigger) {
      if (!this.mobile.matches || this.isOpen) return;
      this.prepareImages();
      this.trigger = trigger;
      this.scrollPosition = window.scrollY;
      document.body.append(this.panel);
      this.panel.hidden = false;
      this.lockLayout();
      this.background = [...document.querySelectorAll('main, #MainContent, footer, .section-footer')]
        .filter(el => !el.contains(this.header) && !el.contains(this.panel));
      this.originalInert = new Map(this.background.map(el => [el, el.inert]));
      this.background.forEach(el => { el.inert = true; });
      this.positionPanel();
      this.updateTriggers();
      (this.panel.querySelector('summary, a[href]') || this.panel).focus({preventScroll: true});
    }
    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.panel.hidden = true;
      this.append(this.panel);
      this.unlockLayout();
      this.originalInert?.forEach((value, el) => { el.inert = value; });
      this.updateTriggers();
      if (restoreFocus && this.mobile.matches) this.trigger?.focus({preventScroll: true});
      if (window.scrollY !== this.scrollPosition) window.scrollTo({top: this.scrollPosition, left: 0, behavior: 'instant'});
      this.trigger = null;
    }
    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      if (this.imagesIdle != null) window.cancelIdleCallback?.(this.imagesIdle);
      if (this.imagesTimer != null) window.clearTimeout(this.imagesTimer);
      this.imagesScheduled = false;
      this.resizeObserver?.disconnect();
      this.triggers?.forEach(trigger => this.restoreTrigger(trigger));
      this.controller = null;
    }
  }
  customElements.define('wohnjoy-mobile-menu', WohnjoyMobileMenu);
})();
